import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { Drawer } from 'primeng/drawer';
import { MultiSelect } from 'primeng/multiselect';
import { IndicatorsService } from '@modules/indicadores/services/indicators.service';
import { Indicador, PessoaResponsavel } from '@core/models/indicator.model';
import { AuthService } from '@core/services/auth.service';
import { DataTransferService, ImportSummary } from '@core/services/data-transfer.service';

@Component({
  selector: 'app-indicators-dashboard',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, RouterLink, ButtonModule, DialogModule, TagModule, TooltipModule, Drawer, MultiSelect],
  templateUrl: './indicators-dashboard.html',
  styleUrl: './indicators-dashboard.css',
})
export class IndicatorsDashboard implements OnInit {
  private readonly router = inject(Router);
  protected readonly indicatorsService = inject(IndicatorsService);
  protected readonly authService = inject(AuthService);
  protected readonly dataTransferService = inject(DataTransferService);

  protected readonly showMobileMenu = signal(false);
  protected readonly role = this.authService.role;
  readonly indicators = this.indicatorsService.indicators;
  readonly responsaveisPessoas = this.indicatorsService.responsaveisPessoas;
  readonly loading = this.indicatorsService.loading;

  // Tabs
  readonly activeTab = signal<'ativos' | 'arquivados' | 'responsaveis'>('ativos');

  readonly activeIndicators = computed(() => {
    return this.indicators().filter(i => i.ativo !== false);
  });

  readonly archivedIndicators = computed(() => {
    return this.indicators().filter(i => i.ativo === false);
  });

  // Busca na aba Por Responsável
  protected readonly searchResponsavelQuery = signal('');

  readonly filteredResponsaveisPessoas = computed(() => {
    const list = this.responsaveisPessoas();
    const query = this.searchResponsavelQuery().trim().toLowerCase();
    if (!query) return list;
    return list.filter(p => p.email.toLowerCase().includes(query));
  });

  // Indicadores formatados para MultiSelect
  readonly indicatorOptions = computed(() => {
    return this.activeIndicators().map(ind => ({
      label: `[${ind.sigla}] ${ind.nome}`,
      value: ind.id,
      sigla: ind.sigla,
      nome: ind.nome
    }));
  });

  // Modal de Gerenciar Responsável
  protected readonly showManageModal = signal(false);
  protected readonly selectedPessoaToManage = signal<PessoaResponsavel | null>(null);
  protected readonly manageEmail = signal('');
  protected readonly manageSelectedIndicadorIdsToAdd = signal<string[]>([]);
  protected readonly submittingManage = signal(false);
  protected readonly manageError = signal('');

  readonly availableIndicatorsToAdd = computed(() => {
    const pessoa = this.selectedPessoaToManage();
    if (!pessoa) return this.indicatorOptions();
    const existingIds = new Set(pessoa.indicadores.map(i => String(i.id)));
    return this.indicatorOptions().filter(opt => !existingIds.has(String(opt.value)));
  });

  // Modal de Novo Responsável
  protected readonly showNewResponsavelModal = signal(false);
  protected readonly newResponsavelEmail = signal('');
  protected readonly newResponsavelIndicadorIds = signal<string[]>([]);
  protected readonly submittingNewResponsavel = signal(false);
  protected readonly newResponsavelError = signal('');

  // Modal de Exclusão Definitiva (Apenas Arquivados)
  protected readonly showDeleteConfirmModal = signal(false);
  protected readonly indicatorToDelete = signal<Indicador | null>(null);
  protected readonly deletingIndicator = signal(false);
  protected readonly deleteError = signal('');

  // Modal de Importação
  protected readonly showImportModal = signal(false);
  protected readonly selectedFile = signal<File | null>(null);
  protected readonly importSummary = signal<ImportSummary | null>(null);
  protected readonly importMode = signal<'AUTO' | 'LEGACY' | 'EXPORT'>('AUTO');
  protected readonly cleanImport = signal(false);

  // Modal de Lançamento de Coleta Individual
  protected readonly showCreateCollectionModal = signal(false);
  protected readonly indicatorForCollection = signal<Indicador | null>(null);
  protected readonly submittingCollection = signal(false);
  protected readonly collectionError = signal('');
  protected readonly collectionForm = signal({ referencia: '' });
  protected readonly indicatorToArchive = signal<Indicador | null>(null);

  getTableLabel(key: string): string {
    const map: Record<string, string> = {
      indicadores: 'Indicadores',
      responsaveis: 'Responsáveis',
      coletas_mensais: 'Coletas Mensais',
      coletas_historico: 'Histórico de Coletas',
      indicadores_historico: 'Histórico de Indicadores'
    };
    return map[key] || key;
  }

  ngOnInit() {
    this.indicatorsService.loadAll();
  }

  async exportXlsx() {
    try {
      await this.dataTransferService.exportToXlsx();
    } catch (err: any) {
      alert('Erro ao exportar base de dados: ' + (err.message || err));
    }
  }

  openImportModal() {
    this.selectedFile.set(null);
    this.importSummary.set(null);
    this.importMode.set('AUTO');
    this.cleanImport.set(false);
    this.showImportModal.set(true);
  }

  closeImportModal() {
    this.showImportModal.set(false);
    this.selectedFile.set(null);
    this.importSummary.set(null);
    this.importMode.set('AUTO');
    this.cleanImport.set(false);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile.set(input.files[0]);
    }
  }

  async submitImport() {
    const file = this.selectedFile();
    if (!file) return;

    try {
      const summary = await this.dataTransferService.importFromXlsx(file, this.importMode(), this.cleanImport());
      this.importSummary.set(summary);
      await this.indicatorsService.loadAll();
    } catch (err: any) {
      alert('Erro ao importar planilha: ' + (err.message || err));
    }
  }

  // --- GERENCIAMENTO DE RESPONSÁVEIS (ABA "POR RESPONSÁVEL") ---

  openNewResponsavelModal() {
    this.newResponsavelEmail.set('');
    this.newResponsavelIndicadorIds.set([]);
    this.newResponsavelError.set('');
    this.showNewResponsavelModal.set(true);
  }

  closeNewResponsavelModal() {
    this.showNewResponsavelModal.set(false);
    this.newResponsavelEmail.set('');
    this.newResponsavelIndicadorIds.set([]);
    this.newResponsavelError.set('');
  }

  async saveNewResponsavel() {
    const email = this.newResponsavelEmail().trim().toLowerCase();
    const ids = this.newResponsavelIndicadorIds();

    if (!email || !email.includes('@')) {
      this.newResponsavelError.set('Por favor, informe um e-mail institucional válido.');
      return;
    }

    if (!ids || ids.length === 0) {
      this.newResponsavelError.set('Selecione pelo menos um indicador para vincular ao novo responsável.');
      return;
    }

    this.submittingNewResponsavel.set(true);
    this.newResponsavelError.set('');
    try {
      await this.indicatorsService.createResponsavel(email, ids);
      this.closeNewResponsavelModal();
    } catch (err: any) {
      this.newResponsavelError.set(err.error?.message || err.message || 'Erro ao criar responsável.');
    } finally {
      this.submittingNewResponsavel.set(false);
    }
  }

  openManageModal(pessoa: PessoaResponsavel) {
    this.selectedPessoaToManage.set(pessoa);
    this.manageEmail.set(pessoa.email);
    this.manageSelectedIndicadorIdsToAdd.set([]);
    this.manageError.set('');
    this.showManageModal.set(true);
  }

  closeManageModal() {
    this.showManageModal.set(false);
    this.selectedPessoaToManage.set(null);
    this.manageEmail.set('');
    this.manageSelectedIndicadorIdsToAdd.set([]);
    this.manageError.set('');
  }

  async saveManageEmail() {
    const pessoa = this.selectedPessoaToManage();
    if (!pessoa) return;

    const emailAtual = pessoa.email;
    const novoEmail = this.manageEmail().trim().toLowerCase();

    if (!novoEmail || !novoEmail.includes('@')) {
      this.manageError.set('Por favor, informe um e-mail válido.');
      return;
    }

    if (emailAtual === novoEmail) {
      return;
    }

    this.submittingManage.set(true);
    this.manageError.set('');
    try {
      await this.indicatorsService.updateResponsavelEmail(emailAtual, novoEmail);
      // Atualizar o objeto local
      const updatedPessoas = await this.indicatorsService.loadAll();
      const updated = this.responsaveisPessoas().find(p => p.email === novoEmail);
      if (updated) {
        this.selectedPessoaToManage.set(updated);
        this.manageEmail.set(updated.email);
      }
    } catch (err: any) {
      this.manageError.set(err.error?.message || err.message || 'Erro ao atualizar e-mail.');
    } finally {
      this.submittingManage.set(false);
    }
  }

  async removeIndicadorFromManagePessoa(indicadorId: string) {
    const pessoa = this.selectedPessoaToManage();
    if (!pessoa) return;

    this.submittingManage.set(true);
    this.manageError.set('');
    try {
      await this.indicatorsService.removeIndicadorFromPessoa(pessoa.email, indicadorId);
      
      // Atualiza o estado da pessoa em gestão
      const updatedList = this.responsaveisPessoas();
      const updated = updatedList.find(p => p.email.toLowerCase() === pessoa.email.toLowerCase());
      
      if (updated && updated.totalIndicadores > 0) {
        this.selectedPessoaToManage.set(updated);
      } else {
        // Se removeu o último indicador, fecha o modal pois a pessoa não tem mais vínculos
        this.closeManageModal();
      }
    } catch (err: any) {
      this.manageError.set(err.error?.message || err.message || 'Erro ao desvincular indicador.');
    } finally {
      this.submittingManage.set(false);
    }
  }

  async addIndicadoresToManagePessoa() {
    const pessoa = this.selectedPessoaToManage();
    const ids = this.manageSelectedIndicadorIdsToAdd();
    if (!pessoa || !ids || ids.length === 0) return;

    this.submittingManage.set(true);
    this.manageError.set('');
    try {
      await this.indicatorsService.addIndicadoresToPessoa(pessoa.email, ids);
      this.manageSelectedIndicadorIdsToAdd.set([]);
      
      const updated = this.responsaveisPessoas().find(p => p.email.toLowerCase() === pessoa.email.toLowerCase());
      if (updated) {
        this.selectedPessoaToManage.set(updated);
      }
    } catch (err: any) {
      this.manageError.set(err.error?.message || err.message || 'Erro ao adicionar indicadores.');
    } finally {
      this.submittingManage.set(false);
    }
  }

  // --- EXCLUSÃO DEFINITIVA (ARQUIVADOS) ---

  openDeleteConfirmation(indicator: Indicador) {
    this.indicatorToDelete.set(indicator);
    this.deleteError.set('');
    this.showDeleteConfirmModal.set(true);
  }

  closeDeleteConfirmation() {
    this.showDeleteConfirmModal.set(false);
    this.indicatorToDelete.set(null);
    this.deleteError.set('');
  }

  async confirmDeleteIndicator() {
    const indicator = this.indicatorToDelete();
    if (!indicator) return;

    this.deletingIndicator.set(true);
    this.deleteError.set('');
    try {
      await this.indicatorsService.deletePermanente(indicator.id);
      this.closeDeleteConfirmation();
    } catch (err: any) {
      this.deleteError.set(err.error?.message || err.message || 'Erro ao excluir indicador definitivamente.');
    } finally {
      this.deletingIndicator.set(false);
    }
  }

  // --- ARQUIVAR / REATIVAR ---

  openArchiveConfirmation(indicator: Indicador) {
    this.indicatorToArchive.set(indicator);
  }

  closeArchiveConfirmation() {
    this.indicatorToArchive.set(null);
  }

  async confirmArchiveIndicator() {
    const indicator = this.indicatorToArchive();
    if (!indicator) return;
    await this.indicatorsService.setAtivo(indicator.id, false);
    this.closeArchiveConfirmation();
  }

  async unarchiveIndicator(indicator: Indicador) {
    await this.indicatorsService.setAtivo(indicator.id, true);
  }

  // --- COLETA INDIVIDUAL ---

  openCreateCollectionModal(indicator: Indicador) {
    this.indicatorForCollection.set(indicator);
    this.collectionError.set('');
    
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    this.collectionForm.set({ referencia: indicator.periodicidade === 'ANUAL' ? String(now.getFullYear()) : currentMonth });
    this.showCreateCollectionModal.set(true);
  }

  closeCreateCollectionModal() {
    this.showCreateCollectionModal.set(false);
    this.indicatorForCollection.set(null);
    this.collectionError.set('');
  }

  async confirmCreateCollection() {
    const indicator = this.indicatorForCollection();
    const form = this.collectionForm();
    if (!indicator || !form.referencia) return;

    this.submittingCollection.set(true);
    this.collectionError.set('');
    try {
      await this.indicatorsService.createPendingCollection(indicator.id, indicator.periodicidade || 'MENSAL', form.referencia);
      this.closeCreateCollectionModal();
      alert(`Coleta de referência ${form.referencia} criada com sucesso para [${indicator.sigla}] ${indicator.nome}!`);
    } catch (err: any) {
      this.collectionError.set(err.message || 'Erro ao criar coleta individual.');
    } finally {
      this.submittingCollection.set(false);
    }
  }

  async logout() {
    await this.authService.logout();
    this.router.navigate(['/login']);
  }

  editIndicator(id: string) {
    this.router.navigate(['/indicadores', id, 'editar']);
  }
}
